import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  SelectChangeEvent,
  Stack,
  Divider,
  Alert,
  FormHelperText
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { 
  GLAccount, 
  accountTypes, 
  accountTypeRanges,
  getGLAccountById,
  saveGLAccount,
  generateNextAccountNumber
} from '../firebase/glAccountServices';
import { useSnackbar } from 'notistack';

interface GLAccountFormProps {
  open: boolean;
  onClose: () => void;
  account?: GLAccount;
  level: number;
  parentAccount?: string;
  onSave: () => void;
}

const GLAccountForm: React.FC<GLAccountFormProps> = ({
  open,
  onClose,
  account,
  level,
  parentAccount,
  onSave
}) => {
  // Form state
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');
  const [accountType, setAccountType] = useState<string>('Assets');
  const [description, setDescription] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [balanceType, setBalanceType] = useState<'debit' | 'credit'>('debit');
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [parentAccountDetails, setParentAccountDetails] = useState<GLAccount | null>(null);
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Load account data when form opens
  useEffect(() => {
    if (open) {
      if (account) {
        // Edit mode - load existing account data
        setAccountNumber(account.accountNumber || '');
        setAccountName(account.accountName || '');
        setAccountType(account.accountType || 'Assets');
        setDescription(account.description || '');
        setIsActive(account.isActive !== undefined ? account.isActive : true);
        setBalanceType(account.balanceType || 'debit');
        setCurrentBalance(account.currentBalance || 0);
      } else {
        // Create mode - reset form
        setAccountName('');
        setDescription('');
        setAccountType('Assets');
        setBalanceType('debit');
        setIsActive(true);
        setCurrentBalance(0);
        
        // Generate account number based on level and parent
        generateAccountNumber();
      }
      
      // Load parent account details for reference only
      if (parentAccount) {
        loadParentAccount();
      }
    }
  }, [open, account, level, parentAccount]);
  
  // Generate account number
  const generateAccountNumber = async () => {
    if (!open) return;
    
    try {
      setLoading(true);
      const nextNumber = await generateNextAccountNumber(
        parentAccount,
        level === 1 ? accountType : undefined,
        level
      );
      setAccountNumber(nextNumber);
    } catch (error: any) {
      enqueueSnackbar(`Error generating account number: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Load parent account details for reference only
  const loadParentAccount = async () => {
    if (!parentAccount || !open) return;
    
    try {
      setLoading(true);
      const parentDoc = await getGLAccountById(parentAccount);
      
      if (parentDoc) {
        // Just store parent details for reference
        setParentAccountDetails(parentDoc);
      }
    } catch (error) {
      console.error('Error loading parent account:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    // Validate required fields
    if (!accountNumber || !accountName) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }
    
    // Make sure account type is set for level 1, or inherited for levels > 1
    if (level === 1 && !accountType) {
      enqueueSnackbar('Please select an account type', { variant: 'error' });
      return;
    }
    
    setSaving(true);
    
    try {
      // For sub-accounts, ensure we're using the parent's account type and balance type
      let finalAccountType = accountType;
      let finalBalanceType = balanceType;
      
      if (level > 1 && parentAccountDetails) {
        console.log('Using parent account details for submission');
        finalAccountType = parentAccountDetails.accountType;
        finalBalanceType = parentAccountDetails.balanceType || 
          (parentAccountDetails.accountType === 'Assets' || parentAccountDetails.accountType === 'Expenses' ? 'debit' : 'credit');
        
        console.log(`Final values for submission: Type=${finalAccountType}, Balance=${finalBalanceType}`);
      }
      
      // Prepare account data
      // Prepare account data without ID field for new accounts
      const accountData: GLAccount = {
        // Only include ID if it's a valid string (editing existing account)
        ...(account?.id ? { id: account.id } : {}),
        accountNumber: accountNumber,
        accountName: accountName,
        accountType: finalAccountType || 'Assets', // Ensure we have a value
        description: description || '',
        isActive: isActive,
        balanceType: finalBalanceType || 'debit', // Ensure we have a value
        level: level,
        parentAccount: level > 1 ? parentAccount || '' : '', // Empty string instead of null
        currentBalance: level === 3 ? currentBalance : 0,
      };
      
      console.log('Saving account data:', accountData);
      
      // Save account
      await saveGLAccount(accountData);
      
      // Close form and notify success
      handleClose();
      onSave();
    } catch (error: any) {
      console.error('Error saving account:', error);
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };
  
  // Handle dialog close
  const handleClose = () => {
    setErrors({});
    onClose();
  };
  
  // Handle account type change
  const handleAccountTypeChange = async (event: SelectChangeEvent<string>) => {
    const newType = event.target.value;
    setAccountType(newType);
    
    // Update balance type based on account type
    if (newType === 'Assets' || newType === 'Expenses') {
      setBalanceType('debit');
    } else if (newType === 'Liabilities' || newType === 'Equity' || newType === 'Revenue') {
      setBalanceType('credit');
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {account ? 'Edit GL Account' : `Add ${level === 1 ? 'Main' : level === 2 ? 'Sub' : 'Detail'} Account`}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2">Loading account data...</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1 }}>
            {parentAccountDetails && (
              <Box mb={3}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Parent Account: {parentAccountDetails.accountNumber} - {parentAccountDetails.accountName}
                  </Typography>
                </Alert>
              </Box>
            )}
            
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <TextField
                  label="Account Number"
                  fullWidth
                  value={accountNumber}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={loading}
                  helperText={"Auto-generated based on account type and hierarchy"}
                  margin="normal"
                />
                
                <TextField
                  label="Account Name"
                  fullWidth
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  error={!!errors.accountName}
                  helperText={errors.accountName}
                  margin="normal"
                  autoFocus
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="account-type-label">Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    value={accountType || ''}
                    onChange={handleAccountTypeChange}
                    label="Account Type"
                  >
                    {accountTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                  {level > 1 && parentAccountDetails && (
                    <FormHelperText>
                      Parent account type: {parentAccountDetails.accountType} (optional to follow)
                    </FormHelperText>
                  )}
                </FormControl>
                
                <FormControl fullWidth margin="normal">
                  <InputLabel id="balance-type-label">Balance Type</InputLabel>
                  <Select
                    labelId="balance-type-label"
                    value={balanceType || 'debit'}
                    onChange={(e) => setBalanceType(e.target.value as 'debit' | 'credit')}
                    label="Balance Type"
                  >
                    <MenuItem value="debit">Debit</MenuItem>
                    <MenuItem value="credit">Credit</MenuItem>
                  </Select>
                  {level > 1 && parentAccountDetails && (
                    <FormHelperText>
                      Parent balance type: {parentAccountDetails.balanceType} (optional to follow)
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
              
              {level === 3 && (
                <TextField
                  label="Current Balance"
                  fullWidth
                  type="number"
                  value={currentBalance}
                  onChange={(e) => setCurrentBalance(Number(e.target.value))}
                  margin="normal"
                  InputProps={{
                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>RM</Typography>
                  }}
                />
              )}
              
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Active Account"
              />
            </Stack>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || saving}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Saving...' : 'Save Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLAccountForm;
